import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, Phone, Mail, ArrowRight, Calendar } from "lucide-react";
import { api } from "@/lib/api";

export const STATUSES = ["New", "In Progress", "Quoted", "Won", "Lost"];
export const STATUS_COLORS = {
  New: "bg-green-100 text-green-700",
  "In Progress": "bg-blue-100 text-blue-700",
  Quoted: "bg-amber-100 text-amber-700",
  Won: "bg-emerald-100 text-emerald-700",
  Lost: "bg-slate-200 text-slate-600",
};

export default function Leads() {
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");

  const { data: leads = [], isLoading } = useQuery({
    queryKey: ["leads", status, search],
    queryFn: async () => (await api.get("/admin/leads", { params: { status, search } })).data,
  });

  return (
    <div data-testid="leads-page">
      <h1 className="font-display font-black text-slate-900 text-2xl sm:text-3xl">Leads</h1>
      <p className="text-slate-500 mt-1">Manage and track your quote requests.</p>

      <div className="mt-6 space-y-3">
        <div className="relative">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4.5 w-4.5 text-slate-400" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, phone, email, postcode…" data-testid="leads-search"
            className="w-full rounded-xl border border-slate-300 bg-white pl-11 pr-4 py-3 outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100" />
        </div>
        <div className="flex flex-wrap gap-2">
          {["all", ...STATUSES].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`leads-filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`rounded-full px-4 py-1.5 text-sm font-semibold transition ${status === s ? "bg-green-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {s === "all" ? "All" : s}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        {isLoading ? (
          <p className="text-slate-500">Loading…</p>
        ) : leads.length === 0 ? (
          <div className="rounded-2xl bg-white border border-slate-200 p-10 text-center text-slate-500">No leads found.</div>
        ) : (
          <ul className="space-y-3">
            {leads.map((l) => (
              <li key={l.id}>
                <Link to={`/admin/leads/${l.id}`} data-testid={`lead-row-${l.id}`} className="block rounded-2xl bg-white border border-slate-200 p-4 sm:p-5 shadow-sm hover:shadow-md hover:border-green-200 transition">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-bold text-slate-900">{l.full_name}</div>
                      <div className="text-sm text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5">
                        <span className="flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{l.phone}</span>
                        {l.email && <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{l.email}</span>}
                        {l.postcode && <span>{l.postcode}</span>}
                      </div>
                      {l.message && <p className="text-sm text-slate-400 mt-2 line-clamp-1">{l.message}</p>}
                    </div>
                    <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${STATUS_COLORS[l.status]}`}>{l.status}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 text-xs text-slate-400">
                    <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{new Date(l.created_at).toLocaleDateString()}</span>
                    <span className="flex items-center gap-1 text-green-700 font-semibold">Open <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
